/**
 * Current User Hook
 *
 * Provides the current logged-in user's information for display purposes.
 * Handles both demo and live modes with appropriate fallbacks.
 */

import { useMemo } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { getModuleMode } from '@/lib/feature-mode';
import { demoUsers } from '@/data/demo-teams';

export interface CurrentUser {
  name: string;
  email: string | undefined;
  firstName: string;
}

/**
 * Extracts the first name from an email address.
 * Handles formats like "john.smith@company.com" -> "John"
 * and "johnsmith@company.com" -> "Johnsmith"
 */
function extractFirstNameFromEmail(email: string): string {
  const localPart = email.split('@')[0];
  if (!localPart) return 'User';

  // Split by common separators (dot, underscore, hyphen)
  const nameParts = localPart.split(/[._-]/);
  const firstName = nameParts[0];

  if (!firstName) return 'User';

  // Capitalize first letter
  return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
}

/**
 * Hook to get the current user's information.
 *
 * Priority order:
 * 1. If email is available from AuthProvider (user is authenticated), use it
 * 2. If in demo mode and no email, return demo user data
 * 3. Otherwise, fall back to generic "User"
 *
 * This ensures authenticated users always see their name, regardless of mode settings.
 */
export function useCurrentUser(): CurrentUser {
  const { email } = useAuth();
  
  // Check the dashboard module's mode setting for demo mode fallback
  const dashboardMode = getModuleMode('dashboard');
  const isDemo = dashboardMode === 'demo';

  const currentUser = useMemo<CurrentUser>(() => {
    // Priority 1: If we have an email from AuthProvider, the user is authenticated
    // Always use their email to derive their name, regardless of mode settings
    if (email) {
      const firstName = extractFirstNameFromEmail(email);
      return {
        name: firstName,
        email,
        firstName,
      };
    }

    // Priority 2: If in demo mode and no email, return demo user data
    if (isDemo) {
      const demoUser = demoUsers[0];
      if (demoUser) {
        const firstName = demoUser.name.split(' ')[0] ?? 'User';
        return {
          name: demoUser.name,
          email: demoUser.email,
          firstName,
        };
      }
      return {
        name: 'Demo User',
        email: undefined,
        firstName: 'Demo',
      };
    }

    // Priority 3: No email and not in demo mode - return generic "User"
    return {
      name: 'User',
      email: undefined,
      firstName: 'User',
    };
  }, [isDemo, email]);

  return currentUser;
}
